import { createContext, useContext, type ReactNode } from "react";
import type { PublicOrganizationArticle, PublicOrganizationProfile } from "@/lib/publicOrganizationProfile";
import type { DirectoryEntry } from "@/lib/brokerageDirectory";

/**
 * Data fetched BEFORE a build-time render, handed to the page tree.
 *
 * renderToString is synchronous and runs no effects, so a page that fetched
 * in useQuery would prerender as its loading skeleton. scripts/prerender.mjs
 * therefore fetches each public organization first and mounts the route with
 * that data here; the page reads it as `initialData`, so the same component
 * renders complete markup in Node and fetches for itself in the browser,
 * where this provider is never mounted and the context is empty.
 */
export interface PrerenderData {
  organization?: PublicOrganizationProfile | null;
  article?: PublicOrganizationArticle | null;
  /** The greater Sacramento directory (#1050), built from the profiles the prerender pass fetched. */
  brokerageDirectory?: DirectoryEntry[];
}

const PrerenderDataContext = createContext<PrerenderData>({});

export function PrerenderDataProvider({ value, children }: { value: PrerenderData; children: ReactNode }) {
  return <PrerenderDataContext.Provider value={value}>{children}</PrerenderDataContext.Provider>;
}

export function usePrerenderData(): PrerenderData {
  return useContext(PrerenderDataContext);
}
